<template>
  <div class="card mb-0" style="min-width: 450px">
    <div class="px-4 pt-4 text-center">
      <h4 class="text-muted" style="font-size: 14px">Mentions Over Time</h4>
    </div>
    <div id="competitionMentionTimeChart" class="pb-3 px-4" style="height: 240px">
      <apexchart type="line" height="100%" :series="formattedMentions" :options="chartOptions" />
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import moment from 'moment-timezone'
const CompetitionDetailsModule = createNamespacedHelpers('competitionDetails')

export default {
  name: 'CompetitionMentionsOverTime',
  computed: {
    ...CompetitionDetailsModule.mapGetters(['selectMentions', 'selectGranularity']),
    formattedMentions() {
      // Create a map to store aggregated data
      const aggregatedData = {
        POSITIVE: {},
        NEGATIVE: {}
      }

      // First pass: aggregate the data
      Object.keys(this.selectMentions).forEach((date) => {
        let formattedDate
        if (this.selectGranularity === 'week') {
          // For weekly view, use the start of the week
          const weekStart = moment(date).startOf('week')
          const weekEnd = moment(date).endOf('week')
          formattedDate = `${weekStart.format('YYYY-MM-DD')}_${weekEnd.format('YYYY-MM-DD')}`
        } else if (this.selectGranularity === 'year') {
          // For yearly view, use the year
          formattedDate = moment(date).format('YYYY')
        } else if (this.selectGranularity === 'month') {
          // For monthly view, use the first day of the month
          formattedDate = moment(date).startOf('month').format('YYYY-MM-DD')
        } else {
          formattedDate = moment(date).format('YYYY-MM-DD')
        }

        ;['POSITIVE', 'NEGATIVE'].forEach((sentiment) => {
          if (!aggregatedData[sentiment][formattedDate]) {
            aggregatedData[sentiment][formattedDate] = 0
          }
          aggregatedData[sentiment][formattedDate] += this.selectMentions[date][sentiment] || 0
        })
      })

      // Get all unique dates
      let allDates = [
        ...new Set([
          ...Object.keys(aggregatedData.POSITIVE),
          ...Object.keys(aggregatedData.NEGATIVE)
        ])
      ]

      // Sort dates based on granularity
      if (this.selectGranularity === 'year') {
        allDates.sort((a, b) => parseInt(a) - parseInt(b))
      } else if (this.selectGranularity === 'week') {
        allDates.sort((a, b) => {
          const [aStart] = a.split('_')
          const [bStart] = b.split('_')
          return moment(aStart).valueOf() - moment(bStart).valueOf()
        })
      } else {
        allDates.sort((a, b) => moment(a).valueOf() - moment(b).valueOf())
      }

      // Create series data
      const negativeData = allDates.map((date) => ({
        x: this.formatDate(date),
        y: aggregatedData.NEGATIVE[date] || 0
      }))

      const positiveData = allDates.map((date) => ({
        x: this.formatDate(date),
        y: aggregatedData.POSITIVE[date] || 0
      }))

      const seriesData = [
        {
          name: 'Negative',
          data: negativeData
        },
        {
          name: 'Positive',
          data: positiveData
        }
      ]

      return seriesData
    },
    chartOptions() {
      const options = {
        chart: {
          height: 240,
          type: 'bar',
          toolbar: { show: false },
          zoom: { enabled: false },
          stacked: false
        },
        colors: ['#FC6D87', '#33D691'],
        grid: { show: false },
        noData: { text: 'No data yet!' },
        dataLabels: { enabled: false },
        xaxis: {
          type: 'category',
          tooltip: { enabled: false },
          tickPlacement: 'on',
          axisTicks: { show: false },
          labels: {
            style: { colors: '#95aac9' },
            rotate: -45,
            rotateAlways: false,
            hideOverlappingLabels: true
          }
        },
        yaxis: {
          axisBorder: { show: true }
        },
        tooltip: {
          enabled: true,
          y: {
            formatter: (value) => {
              return value.toFixed(0)
            }
          }
        },
        stroke: { curve: 'smooth' },
        legend: { show: false }
      }
      return options
    }
  },
  methods: {
    formatDate(date) {
      const granularity = this.selectGranularity

      if (granularity === 'day') {
        return moment(date).format('M/D')
      } else if (granularity === 'week') {
        const [startDate, endDate] = date.split('_')
        const start = moment(startDate)
        const end = moment(endDate)
        return `${start.format('MMM D')} - ${end.format('MMM D')}`
      } else if (granularity === 'month') {
        const totalMonths = Object.keys(this.selectMentions).length
        return totalMonths > 12 ? moment(date).format("MMM 'YY") : moment(date).format('MMM')
      } else if (granularity === 'year') {
        return moment(date).format('YYYY')
      }
      return moment(date).format('MMM YY')
    }
  }
}
</script>
